import { useState } from "react";

/**
 * Year + month grid, opened from a month label.
 *
 * Shared by Dashboard and Calendar. Kept in one place deliberately: two copies
 * of a picker drift, and the month it emits drives every figure on the screen
 * behind it.
 *
 * Renders absolutely positioned — the caller must supply a
 * `position: relative` parent for it to anchor to.
 */

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parseYear(month: string): number {
  return parseInt(month.split("-")[0] ?? "2026", 10);
}

export function MonthPicker({
  value,
  onPick,
  onClose,
}: {
  /** Current month, YYYY-MM. */
  value: string;
  onPick: (month: string) => void;
  onClose: () => void;
}) {
  const [year, setYear] = useState(() => parseYear(value));

  return (
    <>
      {/* Click-away backdrop. Sits under the dialog, over everything else. */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 40 }} aria-hidden="true" />
      <div
        role="dialog"
        aria-label="Select month"
        style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 41,
          width: "260px",
          background: "var(--surface, #fff)",
          border: "1px solid var(--border, #e5e1d8)",
          borderRadius: "12px",
          boxShadow: "0 12px 32px -8px rgba(0,0,0,.25)",
          padding: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <button
            className="btn secondary"
            style={{ height: "28px", padding: "0 10px" }}
            onClick={() => setYear((v) => v - 1)}
            aria-label="Previous year"
          >
            ‹
          </button>
          <span style={{ font: "700 14px/1 var(--font-head)" }}>{year}</span>
          <button
            className="btn secondary"
            style={{ height: "28px", padding: "0 10px" }}
            onClick={() => setYear((v) => v + 1)}
            aria-label="Next year"
          >
            ›
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px" }}>
          {MONTH_ABBR.map((mn, i) => {
            const m = `${year}-${String(i + 1).padStart(2, "0")}`;
            const active = m === value;
            return (
              <button
                key={mn}
                className={active ? "btn primary" : "btn secondary"}
                style={{ height: "34px", fontSize: "13px" }}
                aria-current={active ? "true" : undefined}
                onClick={() => {
                  onPick(m);
                  onClose();
                }}
              >
                {mn}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
